import { isTelemetrySafe } from './telemetrySafe.js';
/**
 * The single scalar/non-scalar decision the whole PHI telemetry boundary rests on: a primitive is
 * kept as-is, anything else is replaced by an opaque marker — except an array, which is kept by
 * mapping this same rule over its elements.
 *
 * PHI only ever reaches a log payload inside a structure — a patient row, a list of chat messages,
 * a profile object — while the values call sites deliberately log (ids, counts, durations, enum
 * statuses) are primitives. An array of primitives (`{ ids: [1, 2, 3] }`) cannot carry a structure
 * and is exactly as safe as the primitives it's built from, so it survives; an array element that is
 * itself a structure (an object, or a nested array containing one) still becomes the opaque marker,
 * because that element is where PHI could actually live. Keeping the rule to one `typeof` check per
 * value is the point: there is no key vocabulary and no recursive walker that could drift from the
 * domain model, so a new PHI-bearing field cannot leak by being unknown to a classifier.
 *
 * The single exception is a value the call site marked with `markTelemetrySafe`, which is the
 * explicit, greppable way to log a structure it assembled itself out of identifiers.
 * @__NO_SIDE_EFFECTS__
 */
export function filterTelemetryValue(value) {
    // `typeof null === 'object'`, so null has to be settled before the dispatch below.
    if (value === null) {
        return null;
    }
    if (isTelemetrySafe(value)) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(entry => filterTelemetryValue(entry));
    }
    switch (typeof value) {
        case 'bigint':
        case 'boolean':
        case 'number':
        case 'string':
        case 'undefined':
            return value;
        default:
            return '[object]';
    }
}
//# sourceMappingURL=filterTelemetryValue.js.map